import type * as Api from '@ViewModels';
import type { Node } from '@xyflow/react';
import { NODE_TYPES, type BaseNodeData, type BehaviorCaseNodeData } from '../types';

export const CANCEL_ON_REPLY_BEHAVIOR_WARNING =
	'Cancel on reply is enabled. When a contact replies, the automation stops immediately, so an ' +
	'"if recipient replies" branch never runs and its actions (like adding a tag) are skipped. We ' +
	'recommend using a reply behavior branch on its own — it both reacts to the reply and stops the ' +
	'rest of the automation.';

function isCancelOnReplyEnabled(template?: Api.IAutomationTemplate): boolean {
	return template?.cancelOnReply === true;
}

function isReplyEngagement(engagementType: unknown): boolean {
	if (typeof engagementType === 'number') {
		return engagementType === 0;
	}

	return typeof engagementType === 'string' && engagementType.toLowerCase() === 'reply';
}

function isReplyBehaviorCaseNode(node: Node<BaseNodeData>): boolean {
	if (node.type !== NODE_TYPES.BEHAVIOR_CASE) {
		return false;
	}

	const data = node.data as Partial<BehaviorCaseNodeData>;
	if (data.isDefault || data.isTimeout) {
		return false;
	}

	const caseType = (data.caseType || '').toLowerCase();
	const caseName = (data.caseName || data.label || '').toLowerCase();

	return (
		caseType.includes('textengagementcasestatement') ||
		isReplyEngagement(data.engagementType) ||
		(caseName.includes('repl') && !caseName.includes('click'))
	);
}

function flowHasReplyBehaviorBranch(nodes: Node<BaseNodeData>[]): boolean {
	return nodes.some(isReplyBehaviorCaseNode);
}

export function hasCancelOnReplyBehaviorConflict(
	template: Api.IAutomationTemplate | undefined,
	nodes: Node<BaseNodeData>[]
): boolean {
	return isCancelOnReplyEnabled(template) && flowHasReplyBehaviorBranch(nodes);
}
